import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import * as yup from "yup";

import api from "services/api";
import ERRORS from "common/errors";
import { setLogo } from "store/slices/profile";
import { promptAlertMessage } from "store/slices/alert";
import validateFileSize from "common/validate-file-size";
import serveRequestErrors from "common/serve-request-errors";
import converReadableFileSize from "common/convert-readable-file-size";
import AppButton from "components/app-button";
import uploadIcon from "assets/images/upload-icon.svg";

const PageCompanyLogo = () => {
	const dispatch = useDispatch();
	const uploadInputRef = useRef();
	const emailLogoUploadInputRef = useRef();
	const logo = useSelector((state) => state.profile.logo);
	const [emailBannerLogo, setEmailBannerLogo] = useState("");
	const initialValues = useMemo(() => ({ file: "" }), []);

	//prettier-ignore
	const formik = useFormik({
		initialValues,
		validationSchema: yup.object({
			file: yup.mixed().required(ERRORS.REQUIRED).test("fileType", ERRORS.PROFILE_IMAGE_TYPE, (v) => !v || ["image/jpeg", "image/png"].includes(v.type))
				.test("fileSize", ERRORS.PROFILE_IMAGE_SIZE, (v) => validateFileSize(v)),
		}),
		onSubmit: (values) => {
			onHandleSubmit(values);
		},
	});
	const fileSize = useMemo(() => formik.values?.file?.size, [formik.values]);
	const previewUrl = useMemo(() => (formik.values?.file ? URL.createObjectURL(formik.values.file) : ""), [formik.values.file]);

	//prettier-ignore
	const emailLogoFormik = useFormik({
		initialValues,
		validationSchema: yup.object({
			file: yup.mixed().required(ERRORS.REQUIRED).test("fileType", ERRORS.PROFILE_IMAGE_TYPE, (v) => !v || ["image/jpeg", "image/png"].includes(v.type))
				.test("fileSize", ERRORS.PROFILE_IMAGE_SIZE, (v) => validateFileSize(v)),
		}),
		onSubmit: (values) => {
			onHandleSubmitEmailLogo(values);
		},
	});
	const emailLogoFileSize = useMemo(() => emailLogoFormik.values?.file?.size, [emailLogoFormik.values]);
	const emailLogoPreviewUrl = useMemo(() => (emailLogoFormik.values?.file ? URL.createObjectURL(emailLogoFormik.values.file) : ""), [emailLogoFormik.values.file]);

	useEffect(() => {
		return () => {
			if (previewUrl) URL.revokeObjectURL(previewUrl);
		};
	}, [previewUrl]);

	useEffect(() => {
		return () => {
			if (emailLogoPreviewUrl) URL.revokeObjectURL(emailLogoPreviewUrl);
		};
	}, [emailLogoPreviewUrl]);

	const onHandleChange = useCallback((event) => {
		const file = event.target.files[0];
		formik.setFieldValue("file", file);
	}, [formik]);

	const onHandleUploadFile = useCallback(() => {
		uploadInputRef.current.click();
	}, []);

	const onHandleClearFile = useCallback(() => {
		formik.setFieldValue("file", "");
		if (uploadInputRef.current) uploadInputRef.current.value = "";
	}, [formik]);

	const onHandleChangeEmailLogoFile = useCallback((event) => {
		const file = event.target.files[0];
		emailLogoFormik.setFieldValue("file", file);
	}, [emailLogoFormik]);

	const onHandleUploadEmailLogoFile = useCallback(() => {
		emailLogoUploadInputRef.current.click();
	}, []);

	const onHandleClearEmailLogoFile = useCallback(() => {
		emailLogoFormik.setFieldValue("file", "");
		if (emailLogoUploadInputRef.current) emailLogoUploadInputRef.current.value = "";
	}, [emailLogoFormik]);

	//prettier-ignore
	const onHandleSubmit = useCallback(async (values) => {
		let response = null;

		try {
			const formData = new FormData();
			formData.append("file", values.file);

			response = await api.post.general.uploadCompanyLogo(formData);
		} catch (error) {
			serveRequestErrors(error);
		} finally {
			formik.setSubmitting(false);
		}

		if (response) {
			dispatch(setLogo(response.url));
			dispatch(promptAlertMessage({ message: "Company logo has been updated successfully" }));
			formik.resetForm();
		}
	}, [dispatch, formik]);

	//prettier-ignore
	const onHandleSubmitEmailLogo = useCallback(async (values) => {
		let response = null;

		try {
			const formData = new FormData();
			formData.append("file", values.file);

			response = await api.post.emailTemplate.uploadBannerLogo(formData);
		} catch (error) {
			serveRequestErrors(error);
		} finally {
			emailLogoFormik.setSubmitting(false);
		}

		if (response) {
			setEmailBannerLogo(response.url);
			dispatch(promptAlertMessage({ message: "Email banner logo has been updated successfully" }));
			emailLogoFormik.resetForm();
		}
	}, [dispatch, emailLogoFormik]);

	const onHandleGetEmailBannerLogo = useCallback(async () => {
		let response = null;

		try {
			response = await api.get.emailTemplate.bannerLogo();
		} catch (error) {
			serveRequestErrors(error);
		}

		if (response) setEmailBannerLogo(response.url);
	}, []);

	useEffect(() => {
		onHandleGetEmailBannerLogo();
	}, [onHandleGetEmailBannerLogo]);

	return (
		<div className="page-company-logo">
			<div className="company-logo">
				<div className="company-logo__cards-row">
					<form className="company-logo__card" onSubmit={formik.handleSubmit}>
						<div className="company-logo__card-header">
							<p className="company-logo__card-title">Upload Company Logo</p>
							<p className="company-logo__card-description">Your logo will be displayed on the top left of HR Portal.</p>
						</div>

						<div className="company-logo__preview-row">
							<div className="company-logo__preview">
								{(previewUrl || logo) && <div className="company-logo__preview-image" style={{ backgroundImage: `url(${previewUrl || logo})` }} />}
								{!previewUrl && !logo && <p className="company-logo__preview-empty">No logo uploaded</p>}
							</div>

							<div className="company-logo__upload">
								<div className="company-logo__upload-top">
									<button type="button" className="company-logo__upload-button" onClick={onHandleUploadFile}>
										<img className="company-logo__upload-icon" src={uploadIcon} alt="" />
										{formik.values?.file?.name || "Choose file"}
									</button>

									<p className="company-logo__upload-meta">{fileSize ? converReadableFileSize(fileSize, true) : "JPG or PNG, up to 5MB"}</p>

									{formik.touched.file && formik.errors.file && <p className="company-logo__error">{formik.errors.file}</p>}

									<input type="file" name="file" accept="image/jpeg, image/jpg, image/png" hidden ref={uploadInputRef} onChange={onHandleChange} />
								</div>

								<div className="company-logo__upload-actions">
									{previewUrl && <AppButton type="button" outline label="Clear" onClick={onHandleClearFile} />}
									<AppButton type="submit" label="Update Logo" disabled={formik.isSubmitting || !formik.values.file} />
								</div>
							</div>
						</div>
					</form>

					<form className="company-logo__card" onSubmit={emailLogoFormik.handleSubmit}>
						<div className="company-logo__card-header">
							<p className="company-logo__card-title">Email Banner Logo</p>
							<p className="company-logo__card-description">This logo is shown at the top of system emails.</p>
						</div>

						<div className="company-logo__preview-row">
							<div className="company-logo__preview">
								{(emailLogoPreviewUrl || emailBannerLogo) && <div className="company-logo__preview-image" style={{ backgroundImage: `url(${emailLogoPreviewUrl || emailBannerLogo})` }} />}
								{!emailLogoPreviewUrl && !emailBannerLogo && <p className="company-logo__preview-empty">No banner logo uploaded</p>}
							</div>

							<div className="company-logo__upload">
								<div className="company-logo__upload-top">
									<button type="button" className="company-logo__upload-button" onClick={onHandleUploadEmailLogoFile}>
										<img className="company-logo__upload-icon" src={uploadIcon} alt="" />
										{emailLogoFormik.values?.file?.name || "Choose file"}
									</button>

									<p className="company-logo__upload-meta">{emailLogoFileSize ? converReadableFileSize(emailLogoFileSize, true) : "JPG or PNG, up to 5MB"}</p>

									{emailLogoFormik.touched.file && emailLogoFormik.errors.file && <p className="company-logo__error">{emailLogoFormik.errors.file}</p>}

									<input type="file" name="file" accept="image/jpeg, image/jpg, image/png" hidden ref={emailLogoUploadInputRef} onChange={onHandleChangeEmailLogoFile} />
								</div>

								<div className="company-logo__upload-actions">
									{emailLogoPreviewUrl && <AppButton type="button" outline label="Clear" onClick={onHandleClearEmailLogoFile} />}
									<AppButton type="submit" label="Update Logo" disabled={emailLogoFormik.isSubmitting || !emailLogoFormik.values.file} />
								</div>
							</div>
						</div>
					</form>
				</div>

				<div className="company-logo__card company-logo__card--disabled">
					<div className="company-logo__card-header">
						<div className="company-logo__card-title-row">
							<p className="company-logo__card-title">System Colour</p>
							<p className="company-logo__badge">Coming soon</p>
						</div>
						<p className="company-logo__card-description">Customise the HR Portal's accent colour to match your brand.</p>
					</div>

					<div className="company-logo__swatches">
						{["#0245a9", "#13a7f4", "#00cf9c", "#7048c4", "#f64444"].map((color) => (
							<span key={color} className="company-logo__swatch" style={{ backgroundColor: color }} />
						))}
					</div>
				</div>
			</div>
		</div>
	);
};

export default PageCompanyLogo;
